// Lightweight GA4 wrapper. index.html loads gtag.js deferred, so on the
// first paint window.gtag may not exist yet — every call is guarded to
// fail silently. Events still fire correctly after the SDK finishes
// loading because the events happen on user interaction (well after
// first paint).
//
// Also writes to window.dataLayer directly as a fallback in case
// gtag is delayed but dataLayer has already been initialised.
//
// Usage:
//   import { trackEvent } from "../lib/analytics";
//   trackEvent("tier_selected", { tier_id: "medium", value: 14.95 });
//
// Convention: event names use GA4 recommended snake_case. Standard
// e-commerce events (`begin_checkout`, `purchase`) follow the exact
// GA4 schema (value, currency, items[]) so they light up the built-in
// e-commerce reports.

export function trackEvent(name, params = {}) {
  try {
    if (typeof window === "undefined") return;
    // Auto-attach the app UI language (nl/en/de) to every event so the
    // funnel can be segmented by language in GA4. Never contains PII.
    let language = params.language;
    if (!language) {
      try { language = localStorage.getItem("bingo_lang") || "nl"; } catch { language = "nl"; }
    }
    const payload = { language, ...params };
    // Prefer gtag() when available — it also mirrors to dataLayer.
    if (typeof window.gtag === "function") {
      window.gtag("event", name, payload);
      return;
    }
    // Fallback: push straight to dataLayer so GTM/GA4 can still read it
    // once gtag finishes loading (queued events are replayed).
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...payload });
    }
  } catch {
    /* GA/adblocker — ignore silently */
  }
}

// Fire an event at most once per browser session (per key). Used for
// milestone events like `bingo_created` and `session_completed` so repeated
// user actions or a page refresh don't inflate the funnel counts.
export function trackEventOnce(name, params = {}, key = null) {
  try {
    const guard = `bingo_evt_once_${key || name}`;
    if (typeof window !== "undefined" && window.sessionStorage) {
      if (sessionStorage.getItem(guard)) return;
      sessionStorage.setItem(guard, "1");
    }
  } catch { /* ignore */ }
  trackEvent(name, params);
}

// Session-scoped scratch pad for handing the selected tier across the
// Stripe redirect (so PaymentSuccess can fire `purchase` with the tier).
const TIER_KEY = "bingo_last_tier";

export function rememberTier(tier) {
  try { sessionStorage.setItem(TIER_KEY, JSON.stringify(tier)); } catch { /* ignore */ }
}

export function recallTier() {
  try {
    const raw = sessionStorage.getItem(TIER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearTier() {
  try { sessionStorage.removeItem(TIER_KEY); } catch { /* ignore */ }
}
